// pipe (site scope): the module-level ERP data-view scope — which single site (facility) the
// user is "standing in", and whether they asked for the combined all-sites view instead.
//
// Module-level on purpose (the blessed Intl-formatter pattern): the ONE fetch core in
// `services/erp.ts` is a free function, so the header source must be reachable without DI. The
// value is kept in sync by `ErpScopeBoundary` (mounted around every ERP route), which resolves
// the ACTIVE workspace's facility from the server's own `/me/provisioning` map — the client only
// ever echoes server truth, and the server treats the header as NARROW-ONLY (a spoofed/foreign
// value can only shrink the view to company-wide rows, never widen it).
import { LiveData } from '@toeverything/infra';

export interface ErpSiteScope {
  /** The active workspace's facility id (server-derived), or null when unbound/unknown. */
  facilityId: string | null;
  /** True = the user explicitly chose the combined all-sites view (persisted). */
  allSites: boolean;
}

// `allSites` is IN-MEMORY and defaults OFF (owner decision 2026-07-29). The consolidated all-sites
// view is a deliberate, super-admin-only, on-demand choice — it must NOT persist across reloads
// (in-memory) NOR silently carry across workspace switches (the sidebar toggle resets it whenever the
// active workspace changes). So standing in a workspace ALWAYS defaults to that workspace's own site;
// a super-admin opts into all-sites per session, per workspace.
export const erpSiteScope$ = new LiveData<ErpSiteScope>({
  facilityId: null,
  allSites: false,
});

export function setScopeFacility(facilityId: string | null) {
  const current = erpSiteScope$.value;
  if (current.facilityId !== facilityId) {
    erpSiteScope$.next({ ...current, facilityId });
  }
}

export function setAllSites(allSites: boolean) {
  const current = erpSiteScope$.value;
  if (current.allSites !== allSites) {
    erpSiteScope$.next({ ...current, allSites });
  }
}

/** The extra request header(s) for the current scope — empty when unscoped/all-sites. */
export function siteScopeHeaders(): Record<string, string> {
  const { facilityId, allSites } = erpSiteScope$.value;
  return facilityId && !allSites ? { 'x-pipe-site-scope': facilityId } : {};
}

/** The cache-identity token for the current scope — scope-local caches key on this so rows
 *  cached under one site view never instant-paint into another. */
export function scopeCacheKey(): string {
  const { facilityId, allSites } = erpSiteScope$.value;
  return facilityId && !allSites ? facilityId : 'all-sites';
}

/** PURE: the active workspace's facility from the provisioning map (null = unbound workspace).
 *  Prefers the server's `viewFacilities` (ALL sites for a super admin — the toggle must engage
 *  in an inherited, non-member site's workspace too); falls back to memberships. */
export function facilityForWorkspace(
  provisioning: {
    facilities: { id: string; name: string; workspaceId: string | null }[];
    viewFacilities?: { id: string; name: string; workspaceId: string | null }[];
  } | null,
  workspaceId: string | null
): { id: string; name: string } | null {
  if (!provisioning || !workspaceId) return null;
  const pool = provisioning.viewFacilities ?? provisioning.facilities;
  const f = pool.find(f => f.workspaceId === workspaceId);
  return f ? { id: f.id, name: f.name } : null;
}
